/**
 * The effective plan of an organization: a pure mirror of `public.org_plan()`
 * in supabase/migrations/20261023120000_billing.sql. The server function is
 * what counts for entitlements; the app uses this mirror to show the same
 * answer from the rows RLS lets it read. Change both together.
 */
import type { PlanId } from "@/lib/pricing/plans";
import {
  ENTITLING_STATUSES,
  PAST_DUE_GRACE_DAYS,
} from "../../../supabase/functions/_shared/stripe-billing";

export interface OverrideRow {
  plan_id: PlanId | null;
  until: string | null;
  accept_test_mode: boolean;
}

export interface SubscriptionRowLite {
  id: string;
  plan_id: PlanId;
  billing_interval: "month";
  status: string;
  livemode: boolean;
  past_due_since: string | null;
  current_period_end: string | null;
  cancel_at: string | null;
  cancel_at_period_end: boolean;
  trial_end: string | null;
}

export interface TrialRow {
  ends_at: string;
}

/** `free`: free access (a customer from before billing, or granted by hand). */
export type PlanSource = "free" | "subscription" | "trial" | "none";

export interface OrgPlan {
  planId: PlanId | null;
  /** `active`, `trialing`, `past_due` or `none`. */
  status: string;
  source: PlanSource;
  /** The subscription that decided it, for the billing page. */
  subscription?: SubscriptionRowLite;
}

const DAY = 86_400_000;

export function isEntitling(sub: SubscriptionRowLite, now: Date): boolean {
  if (sub.status === "active" || sub.status === "trialing") return true;
  if (sub.status !== "past_due" || !sub.past_due_since) return false;
  return new Date(sub.past_due_since).getTime() > now.getTime() - PAST_DUE_GRACE_DAYS * DAY;
}

/**
 * Precedence: free access > entitling linked subscription (live mode unless the
 * org accepts test mode) > the owner's app trial as Firma > none.
 */
export function resolveOrgPlan(input: {
  override: OverrideRow | null;
  /** Subscriptions linked to the org. */
  subscriptions: SubscriptionRowLite[];
  /** The org owner's app trial, if any. */
  ownerTrial: TrialRow | null;
  now: Date;
}): OrgPlan {
  const { override, subscriptions, ownerTrial, now } = input;

  if (override?.plan_id && (!override.until || new Date(override.until) > now)) {
    return { planId: override.plan_id, status: "active", source: "free" };
  }

  const acceptTest = override?.accept_test_mode ?? false;
  const sub = subscriptions.find(
    (s) =>
      (s.livemode || acceptTest) && ENTITLING_STATUSES.includes(s.status) && isEntitling(s, now)
  );
  if (sub)
    return { planId: sub.plan_id, status: sub.status, source: "subscription", subscription: sub };

  if (ownerTrial && new Date(ownerTrial.ends_at) > now) {
    return { planId: "firma", status: "trialing", source: "trial" };
  }

  return { planId: null, status: "none", source: "none" };
}

/** Whole days left, rounded up ("zbývá 3 dny" until the last hour); 0 once over. */
export function daysLeft(endsAt: string | null, now: Date): number {
  if (!endsAt) return 0;
  const ms = new Date(endsAt).getTime() - now.getTime();
  return ms <= 0 ? 0 : Math.ceil(ms / DAY);
}

/** When a scheduled cancellation takes effect (flexible mode reports `cancel_at`). */
export function scheduledCancel(sub: SubscriptionRowLite): string | null {
  if (sub.cancel_at) return sub.cancel_at;
  if (sub.cancel_at_period_end) return sub.current_period_end;
  return null;
}
